import { Box, type SxProps, type Theme } from "@mui/material";

export interface DividerProps {
  /**
   * Orientation of the divider
   * @default "horizontal"
   */
  orientation?: "horizontal" | "vertical";
  /**
   * Additional sx props for customization
   */
  sx?: SxProps<Theme>;
}

/**
 * Divider component for visual separation between content sections.
 * Renders as a 1px line using the theme's border color.
 *
 * @example
 * // Horizontal divider (default)
 * <Divider />
 *
 * @example
 * // Vertical divider
 * <Divider orientation="vertical" />
 */
export function Divider(props: DividerProps) {
  const { orientation = "horizontal", sx } = props;

  const sxArray: Array<SxProps<Theme>> = (() => {
    if (Array.isArray(sx)) {
      return sx as Array<SxProps<Theme>>;
    }
    if (sx) {
      return [sx];
    }
    return [];
  })();

  return (
    <Box
      role="separator"
      aria-orientation={orientation}
      sx={
        [
          (theme: Theme) =>
            orientation === "horizontal"
              ? {
                  width: "100%",
                  height: "1px",
                  borderTop: `1px solid ${theme.palette.border?.base}`,
                }
              : {
                  width: "1px",
                  height: "100%",
                  borderLeft: `1px solid ${theme.palette.border?.base}`,
                },
          ...sxArray,
        ] as SxProps<Theme>
      }
    />
  );
}
